import {
  httpDelete,
  httpGet,
  httpPost,
  httpPut,
} from '../../../tools/http/httpClient';
import {
  buildFixtureConflicts,
  buildFixtureManeuvers,
  buildFixtureOptimizedPlan,
} from '../data/v2Fixtures';

const WORKFLOW_STORAGE_PREFIX = 'semafix.intersectionWorkflow.';
const TRAFFIC_LIGHT_STORAGE_PREFIX = 'semafix.intersectionTrafficLights.';
const EDITOR_STATE_PREFIX = 'semafix.editorState.';

function scopedStorageKey(prefix, intersectionId) {
  const company = localStorage.getItem('company') || 'default';
  return `${prefix}${company}.${intersectionId}`;
}

function workflowStorageKey(intersectionId) {
  return scopedStorageKey(WORKFLOW_STORAGE_PREFIX, intersectionId);
}

function editorStateKey() {
  const company = localStorage.getItem('company') || 'default';
  return `${EDITOR_STATE_PREFIX}${company}`;
}

function readEditorState() {
  try {
    return JSON.parse(localStorage.getItem(editorStateKey()) || '{}');
  } catch {
    return {};
  }
}

function writeEditorState(next) {
  localStorage.setItem(editorStateKey(), JSON.stringify(next));
}

function storedManeuvers(intersectionId) {
  return readEditorState().maneuvers?.[String(intersectionId)] || null;
}

function persistManeuvers(intersectionId, maneuvers) {
  const state = readEditorState();
  writeEditorState({
    ...state,
    maneuvers: { ...(state.maneuvers || {}), [String(intersectionId)]: maneuvers },
  });
}

function localManeuvers(intersectionId) {
  return storedManeuvers(intersectionId) || buildFixtureManeuvers(intersectionId);
}

function findLocalManeuver(id) {
  const state = readEditorState();
  const entries = Object.entries(state.maneuvers || {});
  for (const [intersectionId, maneuvers] of entries) {
    if (maneuvers.some((item) => String(item.id) === String(id))) {
      return { intersectionId, maneuvers };
    }
  }
  const fixtureIntersection = String(id).match(/^fixture-([^-]+)-/)?.[1];
  if (fixtureIntersection) {
    return { intersectionId: fixtureIntersection, maneuvers: buildFixtureManeuvers(fixtureIntersection) };
  }
  return null;
}

export function getIntersectionWorkflowId(intersectionId) {
  return localStorage.getItem(workflowStorageKey(intersectionId));
}

export function rememberIntersectionWorkflow(intersectionId, workflowId) {
  if (intersectionId != null && workflowId != null) {
    localStorage.setItem(workflowStorageKey(intersectionId), String(workflowId));
  }
}

export function loadIntersectionTrafficLights(intersectionId) {
  try {
    const value = JSON.parse(
      localStorage.getItem(scopedStorageKey(TRAFFIC_LIGHT_STORAGE_PREFIX, intersectionId)) || '[]',
    );
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

export function saveIntersectionTrafficLights(intersectionId, trafficLights) {
  localStorage.setItem(
    scopedStorageKey(TRAFFIC_LIGHT_STORAGE_PREFIX, intersectionId),
    JSON.stringify(trafficLights),
  );
}

export async function loadIntersectionManeuvers(intersectionId) {
  const workflowId = getIntersectionWorkflowId(intersectionId);
  if (!workflowId) return localManeuvers(intersectionId);
  try {
    const maneuvers = await httpGet('/api/manovre', {
      loadingKey: `semafix-editor-${intersectionId}`,
      showPercentage: false,
    });
    return (Array.isArray(maneuvers) ? maneuvers : [])
      .filter((maneuver) => String(maneuver.workflow_id) === String(workflowId));
  } catch {
    return localManeuvers(intersectionId);
  }
}

export function createManeuver(intersectionId, maneuver) {
  const workflowId = getIntersectionWorkflowId(intersectionId);
  return httpPost('/api/manovre', {
    ...maneuver,
    workflow_id: workflowId ? Number(workflowId) : undefined,
  }, {
    loadingKey: 'semafix-create-maneuver',
    loadingText: 'Salvataggio manovra…',
    showPercentage: false,
  }).then((saved) => {
    rememberIntersectionWorkflow(intersectionId, saved.workflow_id);
    return saved;
  }).catch(() => {
    const saved = {
      ...maneuver,
      id: `local-${intersectionId}-${Date.now()}`,
      workflow_id: `local-${intersectionId}`,
      isManual: true,
      manual: true,
      inputSource: 'manual',
    };
    persistManeuvers(intersectionId, [...localManeuvers(intersectionId), saved]);
    return saved;
  });
}

export function updateManeuver(id, maneuver) {
  if (String(id).startsWith('fixture-') || String(id).startsWith('local-')) {
    const found = findLocalManeuver(id);
    if (!found) return Promise.resolve({ ...maneuver, id });
    const saved = { ...maneuver, id, isManual: true, manual: true, inputSource: 'manual' };
    persistManeuvers(found.intersectionId, found.maneuvers.map((item) => (
      String(item.id) === String(id) ? saved : item
    )));
    return Promise.resolve(saved);
  }
  return httpPut(`/api/manovre/${id}`, maneuver, {
    loadingKey: `semafix-update-maneuver-${id}`,
    loadingText: 'Aggiornamento manovra…',
    showPercentage: false,
  });
}

export function deleteManeuver(id) {
  if (String(id).startsWith('fixture-') || String(id).startsWith('local-')) {
    const found = findLocalManeuver(id);
    if (found) {
      persistManeuvers(found.intersectionId, found.maneuvers.filter(
        (item) => String(item.id) !== String(id),
      ));
    }
    return Promise.resolve();
  }
  return httpDelete(`/api/manovre/${id}`, {
    loadingKey: `semafix-delete-maneuver-${id}`,
    showPercentage: false,
  });
}

export async function loadConflicts(intersectionId) {
  const state = readEditorState();
  const cached = state.conflicts?.[String(intersectionId)];
  if (Array.isArray(cached)) return cached;
  const workflowId = getIntersectionWorkflowId(intersectionId);
  if (workflowId) {
    try {
      const conflicts = await httpGet(`/api/conflitti/${workflowId}`, {
        loadingKey: `semafix-conflicts-${workflowId}`,
        showPercentage: false,
      });
      if (Array.isArray(conflicts)) return conflicts;
    } catch {
      // Continue with fixture data.
    }
  }
  return buildFixtureConflicts(intersectionId);
}

export function saveConflicts(intersectionId, conflicts) {
  const state = readEditorState();
  writeEditorState({
    ...state,
    conflicts: { ...(state.conflicts || {}), [String(intersectionId)]: conflicts },
  });
  const workflowId = getIntersectionWorkflowId(intersectionId);
  if (!workflowId) return Promise.resolve();
  return httpPost(`/api/conflitti/${workflowId}`, conflicts, {
    loadingKey: `semafix-conflicts-${workflowId}`,
    showPercentage: false,
  }).catch(() => undefined);
}

export function calculateCapacity(payload) {
  return httpPost('/api/calculate_capacity', payload, {
    loadingKey: 'semafix-calculate-capacity',
    loadingText: 'Calcolo capacità…',
    showPercentage: false,
  }).catch(() => {
    const values = payload?.parametri_manovre?.[0] || payload || {};
    const lanes = Math.max(1, Number(values.numero_corsie || values.lanes || 1));
    const laneWidth = Math.max(2.5, Number(values.larghezza_corsia || values.laneWidth || 3.25));
    return { capacita: Math.round(1550 * lanes * Math.min(1.1, laneWidth / 3.25)), source: 'fixture' };
  });
}

export function loadOptimizedPlan(intersectionId) {
  const state = readEditorState();
  return state.plans?.[String(intersectionId)] || buildFixtureOptimizedPlan(intersectionId);
}

export function saveOptimizedPlan(intersectionId, plan) {
  const state = readEditorState();
  const saved = {
    ...plan,
    optimizedAt: new Date().toISOString(),
    source: plan.source || 'optimized',
  };
  writeEditorState({
    ...state,
    plans: { ...(state.plans || {}), [String(intersectionId)]: saved },
  });
  window.dispatchEvent(new CustomEvent('semafix:optimized-plan', {
    detail: { intersectionId: String(intersectionId), plan: saved },
  }));
  return saved;
}

function fixtureOptimization(payload) {
  const cycle = Math.max(1, Math.round(Number(payload.Tc) || 90));
  const yellow = Math.max(0, Math.round(Number(payload.tempogiallo) || 3));
  const phases = (payload.fasi || []).map((phase, index) => {
    const minimum = Number(phase.verde_min_ped || 0);
    const demand = Number(phase.flusso || 0) / Math.max(1, Number(phase.cap_sat || 1200));
    const green = Math.max(minimum, Math.round(Math.max(12, Math.min(cycle - yellow, cycle * (0.28 + demand * 0.25 + index * 0.02)))));
    return {
      nome: phase.nome,
      green,
      yellow,
      red: Math.max(0, cycle - green - yellow),
      verde: green,
    };
  });
  return {
    source: 'fixture',
    cycle,
    optimized_fasi: phases,
    capacità_ottimizzata: Math.round(phases.reduce((sum, phase) => sum + phase.green, 0) * 24),
  };
}

export function optimizePhases(payload, intersectionId) {
  return httpPost('/api/optimize', payload, {
    loadingKey: 'semafix-optimize-phases',
    loadingText: 'Ottimizzazione fasi…',
    showPercentage: false,
  }).catch(() => fixtureOptimization(payload)).then((result) => {
    if (!intersectionId) return result;
    const phases = (result.optimized_fasi || []).map((phase) => {
      const green = Math.round(Number(phase.green ?? phase.verde ?? phase.verde_efficace ?? phase.verde_min_ped) || 0);
      const yellow = Math.round(Number(phase.yellow ?? payload.tempogiallo) || 0);
      return {
        ...phase,
        green,
        yellow,
        red: Math.max(0, Math.round(Number(payload.Tc)) - green - yellow),
      };
    });
    return saveOptimizedPlan(intersectionId, {
      ...result,
      cycle: Math.round(Number(payload.Tc)),
      yellow: Math.round(Number(payload.tempogiallo)),
      allRed: Math.round(Number(payload.tempotuttorosso)),
      phases,
    });
  });
}
